import { KANBAN_STATES } from '../constants';

// Status configurations with colors - unified across the app
const STATUS_CONFIG = {
  not_started: {
    label: 'Not Started',
    color: '#667085',
    bgColor: '#F2F4F7',
  },
  in_progress: {
    label: 'In Progress',
    color: '#2E90FA',
    bgColor: '#EFF8FF',
  },
  in_review: {
    label: 'In Review',
    color: '#9333EA',
    bgColor: '#F3E8FF',
  },
  approved: {
    label: 'Approved',
    color: '#12B76A',
    bgColor: '#ECFDF3',
  },
  scheduled: {
    label: 'Scheduled',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
  },
  published: {
    label: 'Published',
    color: '#06B6D4',
    bgColor: '#ECFEFF',
  },
};

// View mode configurations
export const VIEW_MODE_CONFIG = {
  general: {
    id: 'general',
    label: 'General View',
    field: null,
    type: 'none',
    getGroupKey: null,
    getGroupLabel: null,
    getGroupColor: null,
    getGroupBgColor: null,
    defaultExpanded: [],
    sortOrder: null,
    emptyGroupMessage: null,
  },

  status: {
    id: 'status',
    label: 'Status View',
    field: 'state',
    type: 'enum',
    groups: KANBAN_STATES,
    getGroupKey: (task) => task.state || 'not_started',
    getGroupLabel: (key) => STATUS_CONFIG[key]?.label || key,
    getGroupColor: (key) => STATUS_CONFIG[key]?.color || '#94A3B8',
    getGroupBgColor: (key) => STATUS_CONFIG[key]?.bgColor || '#F1F5F9',
    defaultExpanded: ['in_progress', 'in_review'],
    sortOrder: KANBAN_STATES,
    emptyGroupMessage: (groupLabel) => `No briefs in ${groupLabel}`,
  },

  brand: {
    id: 'brand',
    label: 'Brand View',
    field: 'brand_id',
    type: 'lookup',
    getGroupKey: (task) => {
      // Ensure we return a string key for consistent matching
      if (!task.brand_id) return 'no_brand';
      return String(task.brand_id);
    },
    getGroupLabel: (key, brands = []) => {
      if (key === 'no_brand' || !key) return 'No Brand';

      // Try to find brand by matching key as both string and number
      const brandObj = brands.find(b => {
        const brandId = Object.keys(b)[0];
        return brandId === key || brandId === String(key) || Number(brandId) === Number(key);
      });

      if (brandObj) {
        const brandData = Object.values(brandObj)[0];
        return brandData?.brand_name || brandData || 'Unknown';
      }

      // Fallback: return Unknown Brand with the ID for debugging
      console.warn(`Brand not found for ID: ${key}`, { brands, key });
      return `Unknown Brand (ID: ${key})`;
    },
    getGroupColor: () => '#6366F1',
    getGroupBgColor: () => '#EEF2FF',
    defaultExpanded: [],
    sortOrder: null, // Will be dynamic based on brands
    emptyGroupMessage: (groupLabel) => `No briefs for ${groupLabel}`,
  },

  assignee: {
    id: 'assignee',
    label: 'Assignee View',
    field: 'assigned_to',
    type: 'lookup',
    getGroupKey: (task) => {
      if (!task.assigned_to || task.assigned_to.length === 0) return 'unassigned';
      // For multiple assignees, use the first one
      return task.assigned_to[0]?.id || 'unassigned';
    },
    getGroupLabel: (key, users = []) => {
      if (key === 'unassigned') return 'Unassigned';
      const user = users.find(u => u.id === key);
      return user?.name || user?.email || 'Unknown User';
    },
    getGroupColor: () => '#EC4899',
    getGroupBgColor: () => '#FCE7F3',
    defaultExpanded: [],
    sortOrder: null,
    emptyGroupMessage: (groupLabel) => `No briefs assigned to ${groupLabel}`,
  },

  contentType: {
    id: 'contentType',
    label: 'Content Type View',
    field: 'content_type',
    type: 'enum',
    getGroupKey: (task) => task.content_type || 'no_type',
    getGroupLabel: (key) => {
      if (key === 'no_type') return 'No Content Type';
      // Capitalize and format content type
      return key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },
    getGroupColor: () => '#1D2939',
    getGroupBgColor: () => '#FFFFFF',
    defaultExpanded: [],
    sortOrder: null, // Will be dynamic
    emptyGroupMessage: (groupLabel) => `No ${groupLabel} briefs`,
  },

  campaign: {
    id: 'campaign',
    label: 'Campaign View',
    field: 'project_id',
    type: 'lookup',
    getGroupKey: (task) => task.project_id || 'no_campaign',
    getGroupLabel: (key, projects = []) => {
      if (key === 'no_campaign') return 'No Campaign';
      const project = projects.find(p => p.id === key);
      return project?.name || 'Unknown Campaign';
    },
    getGroupColor: () => '#F97316',
    getGroupBgColor: () => '#FFEDD5',
    defaultExpanded: [],
    sortOrder: null,
    emptyGroupMessage: (groupLabel) => `No briefs in ${groupLabel}`,
  },

  deadline: {
    id: 'deadline',
    label: 'Deadline View',
    field: 'deadline_date',
    type: 'date',
    getGroupKey: (task) => {
      if (!task.deadline_date) return 'no_deadline';

      const deadline = new Date(task.deadline_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      if (deadline < today) return 'overdue';
      if (deadline.toDateString() === today.toDateString()) return 'today';
      if (deadline.toDateString() === tomorrow.toDateString()) return 'tomorrow';
      if (deadline < nextWeek) return 'this_week';
      return 'later';
    },
    getGroupLabel: (key) => {
      const labels = {
        overdue: 'Overdue',
        today: 'Due Today',
        tomorrow: 'Due Tomorrow',
        this_week: 'Due This Week',
        later: 'Due Later',
        no_deadline: 'No Deadline',
      };
      return labels[key] || key;
    },
    getGroupColor: (key) => {
      const colors = {
        overdue: '#EF4444',
        today: '#F59E0B',
        tomorrow: '#3B82F6',
        this_week: '#10B981',
        later: '#94A3B8',
        no_deadline: '#6B7280',
      };
      return colors[key] || '#94A3B8';
    },
    getGroupBgColor: (key) => {
      const bgColors = {
        overdue: '#FEE2E2',
        today: '#FEF3C7',
        tomorrow: '#DBEAFE',
        this_week: '#D1FAE5',
        later: '#F1F5F9',
        no_deadline: '#F3F4F6',
      };
      return bgColors[key] || '#F1F5F9';
    },
    defaultExpanded: ['overdue', 'today', 'tomorrow'],
    sortOrder: ['overdue', 'today', 'tomorrow', 'this_week', 'later', 'no_deadline'],
    emptyGroupMessage: (groupLabel) => `No briefs ${groupLabel.toLowerCase()}`,
  },
};

// Get all available view modes
export const getAvailableViewModes = () => {
  return Object.keys(VIEW_MODE_CONFIG).map(key => ({
    value: key,
    label: VIEW_MODE_CONFIG[key].label,
  }));
};

// Get view mode configuration
export const getViewModeConfig = (viewMode) => {
  return VIEW_MODE_CONFIG[viewMode] || VIEW_MODE_CONFIG.general;
};

// Group tasks by view mode
export const groupTasksByViewMode = (tasks, viewMode, lookupData = {}) => {
  const config = getViewModeConfig(viewMode);
  const grouped = {};

  // Initialize groups
  if (config.sortOrder) {
    config.sortOrder.forEach(key => {
      grouped[key] = [];
    });
  }

  // Group tasks
  tasks.forEach(task => {
    const groupKey = config.getGroupKey(task);
    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    grouped[groupKey].push(task);
  });

  // Convert to array format with metadata
  const groups = Object.entries(grouped).map(([key, tasks]) => {
    // Get lookup data for this view mode (e.g., brands array, users array, projects array)
    const lookup = lookupData[config.field] || [];

    return {
      key,
      label: config.getGroupLabel(key, lookup),
      color: config.getGroupColor(key),
      bgColor: config.getGroupBgColor(key),
      tasks,
      count: tasks.length,
      emptyMessage: config.emptyGroupMessage(config.getGroupLabel(key, lookup)),
    };
  });

  // Sort groups if sort order is defined
  if (config.sortOrder) {
    groups.sort((a, b) => {
      const indexA = config.sortOrder.indexOf(a.key);
      const indexB = config.sortOrder.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  } else {
    // Sort by count descending
    groups.sort((a, b) => b.count - a.count);
  }

  return groups;
};

// Default view mode
export const DEFAULT_VIEW_MODE = 'general';

// LocalStorage key for persisting view mode preference
export const VIEW_MODE_STORAGE_KEY = 'content_planner_view_mode';
